'use client';

import Image from 'next/image';
import Link from 'next/link';
import {
  ImageComponentProps,
  LinkComponentProps,
  ReactConfigContext
} from '@stereoagency/ui';
import { PropsWithChildren, useContext, useRef } from 'react';
import { twMerge } from '@stereoagency/tailwind-config';

const ImageComponent = ({
  alt,
  src,
  sizes,
  width,
  height,
  className
}: ImageComponentProps) => (
  <Image
    alt={alt ?? ''}
    src={src}
    sizes={sizes}
    width={width}
    height={height}
    className={twMerge('w-full', className)}
  />
);

const LinkComponent = ({
  draggable,
  rel,
  title,
  href,
  target,
  className,
  label,
  style,
  children,
  onClick
}: PropsWithChildren<LinkComponentProps>) => {
  const handleClick: typeof onClick = (event) => {
    onClick?.(event);
    // hash link logic
    const current = new URL(window.location.href);
    const url = new URL(event.currentTarget.href, window.location.href);
    if (
      url.hostname === current.hostname &&
      current.pathname === url.pathname &&
      url.hash
    ) {
      event.preventDefault();
      window.location.hash = url.hash;
    }
  };

  return (
    <Link
      rel={rel}
      draggable={draggable}
      href={href}
      title={title}
      target={target}
      className={className}
      style={style}
      onClick={handleClick}
    >
      {children || label}
    </Link>
  );
};

export default function ReactConfigContextProvider({
  children
}: PropsWithChildren) {
  const { config: defaultConfig } = useContext(ReactConfigContext);
  const contextValueRef = useRef({
    config: {
      ...defaultConfig,
      ImageComponent,
      LinkComponent
    }
  });

  return (
    <ReactConfigContext.Provider value={contextValueRef.current}>
      {children}
    </ReactConfigContext.Provider>
  );
}
